import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { ClientContext, ProfileContext } from './contexts';
import { shouldFetchData, status } from '../../data/fetchStatus';
import { quoteStatus } from '../../data/quoteStatus';
import Services from '../Services/services';
import useToast from '../Toasts/useToast';
import {
  ToastHeaders,
  ToastThemes,
  ToastMessages,
} from '../Toasts/ToastOptions/toastOptions';

function ClientProvider() {
  const profileContext = useContext(ProfileContext);
  const enqueueToast = useToast();

  const [quotes, setQuotes] = useState([]);
  const [quotesFetched, setQuotesFetched] = useState(status.DEFAULT);

  const [clientExpertRelations, setClientExpertRelations] = useState(undefined);
  const [clientExpertRelationsFetched, setClientExpertRelationsFetched] =
    useState(status.DEFAULT);

  const [clientExpertRelation, setClientExpertRelation] = useState(undefined);

  const [appointments, setAppointments] = useState([]);
  const [appointmentsFetched, setAppointmentsFetched] = useState(
    status.DEFAULT
  );

  const [reviewExpert, setReviewExpert] = useState({});
  const [reviewExpertFetched, setReviewExpertFetched] = useState(
    status.DEFAULT
  );

  const [reviewPlatform, setReviewPlatform] = useState({});
  const [reviewPlatformFetched, setReviewPlatformFetched] = useState(
    status.DEFAULT
  );

  const [clientRedirected, setClientRedirected] = useState(false);

  // Should check if any data is in fetching state
  // Status : waiting, loading, finished
  const [fetchingCurrentState, setFetchingCurrentState] = useState({
    quotes: 'waiting',
    cers: 'waiting',
    appointments: 'waiting',
    reviewExp: 'waiting',
    reviewPlat: 'waiting',
  });

  const updateFetchingStatus = (key, value) => {
    setFetchingCurrentState((fetchingCurrentState) => ({
      ...fetchingCurrentState,
      ...{ [key]: value },
    }));
  };

  const fetchQuotes = useCallback(async () => {
    if (
      profileContext.client.id === undefined ||
      quotesFetched === status.FETCHED
    )
      return;

    setQuotesFetched(status.FETCHED);
    updateFetchingStatus('quotes', 'loading');

    try {
      const data = await Services.quoteService.quotesByClientID(
        profileContext.client.id
      );

      if (data.length === 0) {
        updateFetchingStatus('quotes', 'finished');
        return;
      }

      setQuotes(data);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    updateFetchingStatus('quotes', 'finished');
  }, [profileContext.client.id, quotesFetched, enqueueToast]);

  const fetchClientExpertRelations = useCallback(async () => {
    if (
      profileContext.client.id === undefined ||
      clientExpertRelationsFetched === status.FETCHED
    )
      return;

    setClientExpertRelationsFetched(status.FETCHED);
    updateFetchingStatus('cers', 'loading');

    try {
      const data = await Services.quoteService.clientExpertRelationByClientID(
        profileContext.client.id,
        true
      );

      if (data.length === 0) {
        updateFetchingStatus('cers', 'finished');
        return;
      }

      setClientExpertRelation(GetCurrentClientExpertRelation(data));
      setClientExpertRelations(data);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    updateFetchingStatus('cers', 'finished');
  }, [profileContext.client.id, clientExpertRelationsFetched, enqueueToast]);

  const fetchAppointments = useCallback(async () => {
    if (
      profileContext.client.id === undefined ||
      appointmentsFetched === status.FETCHED
    )
      return;

    setAppointmentsFetched(status.FETCHED);
    updateFetchingStatus('appointments', 'loading');

    try {
      const data = await Services.appointmentService.appointmentsByClientID(
        profileContext.client.id
      );

      if (data.length === 0) {
        updateFetchingStatus('appointments', 'finished');
        return;
      }

      setAppointments(data);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    updateFetchingStatus('appointments', 'finished');
  }, [profileContext.client.id, appointmentsFetched, enqueueToast]);

  const fetchReviewExpert = useCallback(async () => {
    if (
      profileContext.client.id === undefined ||
      reviewExpertFetched === status.FETCHED
    )
      return;

    setReviewExpertFetched(status.FETCHED);
    updateFetchingStatus('reviewExp', 'loading');

    try {
      const data = await Services.clientReviewService.clientReviewExpert(
        profileContext.client.id
      );

      if (data.length !== 1) {
        setReviewExpert({});
        updateFetchingStatus('reviewExp', 'finished');
        return;
      }

      setReviewExpert(data[0]);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    updateFetchingStatus('reviewExp', 'finished');
  }, [profileContext.client.id, reviewExpertFetched, enqueueToast]);

  const fetchReviewPlatform = useCallback(async () => {
    if (
      profileContext.client.id === undefined ||
      reviewPlatformFetched === status.FETCHED
    )
      return;

    setReviewPlatformFetched(status.FETCHED);
    updateFetchingStatus('reviewPlat', 'loading');

    try {
      const data = await Services.clientReviewService.clientReviewPlatform(
        profileContext.client.id
      );

      if (data.length !== 1) {
        updateFetchingStatus('reviewPlat', 'finished');
        setReviewPlatform({});
        return;
      }

      setReviewPlatform(data[0]);
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    updateFetchingStatus('reviewPlat', 'finished');
  }, [profileContext.client.id, reviewPlatformFetched, enqueueToast]);

  const selectExpert = async (quoteId, expertId, clientId) => {
    try {
      await Services.quoteService.createClientExpertRelation(
        quoteId,
        expertId,
        clientId
      );
      setClientExpertRelationsFetched(status.REFRESH);
      enqueueToast(ToastMessages.CONFIRM_SUCCESS_APPOINTMENT, {
        theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
        header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
        duration: 3000,
      });
    } catch (error) {
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }
  };

  async function selectAppointment(selectedAppointment, success = undefined) {
    let onError = false;

    try {
      if (selectedAppointment !== -1) {
        await Services.appointmentService.updateAppointmentByID(
          selectedAppointment.id,
          { acceptedByClient: 1, acceptedByExpert: 1 }
        );
      }

      const appointmentIdArray = [];
      appointments.forEach((appointment) => {
        if (
          appointment?.clientExpertRelationshipId === clientExpertRelation?.id
        ) {
          if (
            appointment.id !== selectedAppointment.id ||
            selectedAppointment === -1
          )
            appointmentIdArray.push(appointment.id);
        }
      });

      if (appointmentIdArray.length !== 0)
        await Services.appointmentService.deleteAppointment(appointmentIdArray);

      await Services.quoteService.updateClientExpertRelation(
        clientExpertRelation?.id,
        {
          status:
            selectedAppointment === -1
              ? quoteStatus.appointmentAchieved
              : quoteStatus.clientAcceptAppointment,
          statusDate: new Date().toISOString(),
        }
      );
    } catch (error) {
      onError = true;
      enqueueToast(ToastMessages.ERROR_OCCURED_FETCH, {
        theme: ToastThemes.toastThemes.ERROR_OCCURED,
        header: ToastHeaders[ToastThemes.toastThemes.ERROR_OCCURED],
        duration: 3000,
      });
    }

    if (onError) return;

    try {
      await Services.emailService.sendClientAppointmentExpert(
        clientExpertRelation?.expert?.userId,
        'CEC : Un client a accepté un rendez-vous',
        clientExpertRelation?.expert?.firstName,
        clientExpertRelation?.expert?.lastName
      );
    } catch (error) {
      console.error(error);
    }

    fetchAll();

    enqueueToast(ToastMessages.CONFIRM_SUCCESS_APPOINTMENT, {
      theme: ToastThemes.toastThemes.CONFIRM_SUCCESS,
      header: ToastHeaders[ToastThemes.toastThemes.CONFIRM_SUCCESS],
      duration: 3000,
    });

    if (success !== undefined) success();
  }

  function GetCurrentClientExpertRelation(relations) {
    const relation = relations.find(
      (r) => r?.status >= 0 && r?.clientSigned !== -1 && r?.expertSigned !== -1
    );

    return relation;
  }

  function isInStatus(checkedStatus) {
    if (clientExpertRelation === undefined) return false;

    return checkedStatus.includes(clientExpertRelation?.status);
  }

  function refreshClientExpertRelations() {
    setClientExpertRelationsFetched(status.REFRESH);
  }

  function refreshClientExpertReview() {
    setReviewExpertFetched(status.REFRESH);
  }

  function refreshClientPlatformReview() {
    setReviewPlatformFetched(status.REFRESH);
  }

  function refreshAppointments() {
    setAppointmentsFetched(status.REFRESH);
  }

  function fetchAll() {
    setQuotesFetched(status.DEFAULT);
    setClientExpertRelationsFetched(status.DEFAULT);
    setAppointmentsFetched(status.DEFAULT);
    setReviewExpertFetched(status.DEFAULT);
    setReviewPlatformFetched(status.DEFAULT);
  }

  function getRelationByIds(clientExpertRelationId) {
    const relation = clientExpertRelations?.find(
      (rel) => rel.id === clientExpertRelationId
    );

    return relation;
  }

  useEffect(() => {
    if (profileContext?.client?.id) {
      if (shouldFetchData(quotesFetched)) fetchQuotes();

      if (shouldFetchData(clientExpertRelationsFetched))
        fetchClientExpertRelations();

      if (shouldFetchData(appointmentsFetched)) fetchAppointments();

      if (shouldFetchData(reviewExpertFetched)) fetchReviewExpert();

      if (shouldFetchData(reviewPlatformFetched)) fetchReviewPlatform();
    }
  }, [
    profileContext.client.id,
    quotesFetched,
    appointmentsFetched,
    reviewExpertFetched,
    reviewPlatformFetched,
    clientExpertRelationsFetched,
    fetchQuotes,
    fetchAppointments,
    fetchReviewExpert,
    fetchReviewPlatform,
    fetchClientExpertRelations,
  ]);

  return (
    <ClientContext.Provider
      value={{
        clientId: profileContext.client.id,
        quotes,
        clientExpertRelation,
        clientExpertRelations,
        appointments,
        reviewExpert,
        reviewPlatform,
        fetchAll,
        selectExpert,
        isInStatus,
        selectAppointment, // should not be here now
        getRelationByIds,
        refreshClientExpertRelations,
        refreshClientExpertReview,
        refreshClientPlatformReview,
        refreshAppointments,
        client: profileContext?.client,
        fetchingCurrentState,
        redirection: {
          clientRedirected,
          setClientRedirected,
        },
      }}
    >
      <Outlet />
    </ClientContext.Provider>
  );
}

export default ClientProvider;
